import { Head, Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormInput from '@/Components/FormInput';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ClockIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

export default function Index({ timesheets, filters, flash }) {
    const { auth } = usePage().props;
    const userPerms = auth?.user?.permissions || [];
    const isAdmin = auth?.user?.is_admin;
    const can = (perm) => isAdmin || userPerms.includes(perm);

    const [search, setSearch] = useState(filters?.search || '');
    const [dateFrom, setDateFrom] = useState(filters?.date_from || '');
    const [dateTo, setDateTo] = useState(filters?.date_to || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [approveConfirm, setApproveConfirm] = useState(null);

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/timesheets', {
            search,
            date_from: dateFrom,
            date_to: dateTo,
        }, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        setDateFrom('');
        setDateTo('');
        router.get('/timesheets');
    };

    const dayBadge = (day) => {
        if (!day) return <Badge variant="neutral">—</Badge>;
        const weekend = ['friday', 'saturday'];
        const variant = weekend.includes(day.toLowerCase()) ? 'warning' : 'info';
        return <Badge variant={variant}>{day}</Badge>;
    };

    return (
        <AppLayout>
            <Head title="TimeSheet" />
            <PageHeader
                title="TimeSheet"
                subtitle="Daily attendance and overtime tracking"
                actions={can('timesheets.create') ? (
                    <Button href="/timesheets/create" size="sm">
                        <PlusIcon className="w-4 h-4" /> New TimeSheet
                    </Button>
                ) : null}
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3 items-end">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search timesheets..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                            />
                        </div>
                        <FormInput
                            label="From"
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="w-full sm:w-44"
                        />
                        <FormInput
                            label="To"
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="w-full sm:w-44"
                        />
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">
                                <FunnelIcon className="w-4 h-4" /> Apply
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                Reset
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Timesheets Table */}
            <Card>
                <Table minWidth="800px">
                    <thead>
                        <tr>
                            <Th>SL</Th>
                            <Th>Date</Th>
                            <Th>Day</Th>
                            <Th>Employees</Th>
                            <Th>ORD Time</Th>
                            <Th>Total OT</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {timesheets?.map?.((t, idx) => {
                            const totalOt = Number(t.total_overtime || 0);
                            const isApproved = !!t.approved_by;
                            return (
                                <Tr key={t.id}>
                                    <Td className="text-slate-400 text-xs">{timesheets.length - idx}</Td>
                                    <Td className="font-medium text-slate-800 whitespace-nowrap">
                                        {formatDate(t.date)}
                                    </Td>
                                    <Td className="whitespace-nowrap">
                                        {dayBadge(t.day_name)}
                                    </Td>
                                    <Td>
                                        <Badge variant="info">{t.total_entries || t.entries?.length || 0}</Badge>
                                    </Td>
                                    <Td className="font-medium text-slate-700 whitespace-nowrap">
                                        {Number(t.total_ord || 0).toLocaleString()}
                                    </Td>
                                    <Td className="font-medium text-slate-700 whitespace-nowrap">
                                        {totalOt.toLocaleString()}
                                    </Td>
                                    <Td>
                                        {isApproved ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-emerald-800 bg-emerald-50 rounded-full">
                                                <CheckCircleIcon className="w-3.5 h-3.5" /> Approved
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-slate-500 bg-slate-100 rounded-full">
                                                Pending
                                            </span>
                                        )}
                                    </Td>
                                    <Td>
                                        <div className="flex flex-wrap items-center gap-1">
                                            {can('timesheets.approve') && !isApproved && (
                                                <button
                                                    onClick={() => setApproveConfirm(t.id)}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition border border-emerald-200"
                                                >
                                                    <CheckCircleIcon className="w-3.5 h-3.5" /> Approve
                                                </button>
                                            )}
                                            {can('timesheets.edit') && (!isApproved || isAdmin) && (
                                                <Link href={`/timesheets/${t.id}/edit`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition">
                                                    <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                                                </Link>
                                            )}
                                            {can('timesheets.reports') && (
                                                <a href={`/timesheets/${t.id}/pdf`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition">
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> PDF
                                                </a>
                                            )}
                                            {can('timesheets.delete') && (
                                                <button onClick={() => setDeleteConfirm(t.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition">
                                                    <TrashIcon className="w-3.5 h-3.5" /> Delete
                                                </button>
                                            )}
                                        </div>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </tbody>
                </Table>
                {(!timesheets || timesheets.length === 0) && (
                    <EmptyState
                        icon={ClockIcon}
                        title="No timesheets"
                        description="Create your first timesheet to track daily attendance and overtime."
                        action={
                            <Button href="/timesheets/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> New TimeSheet
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Approve Confirmation Modal */}
            <Modal
                show={!!approveConfirm}
                onClose={() => setApproveConfirm(null)}
                title="Approve TimeSheet"
                iconBg="bg-emerald-100"
                iconColor="text-emerald-600"
                icon={<CheckCircleIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to approve this timesheet? Your name will be recorded as the approver.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setApproveConfirm(null)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            router.post(`/timesheets/${approveConfirm}/approve`);
                            setApproveConfirm(null);
                        }}
                    >
                        Approve
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete TimeSheet"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete this timesheet? This will also remove all associated employee entries.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/timesheets/${deleteConfirm}`);
                            setDeleteConfirm(null);
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
